/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Activity, BarChart3, Bot, BookOpen, Boxes, Bug, ChevronDown, ChevronRight, Cpu, Gauge, HeartPulse, KeyRound, Layers, LayoutDashboard, Library, LifeBuoy,
  Landmark, LogOut, Menu as MenuIcon, MessagesSquare, Monitor, MonitorCog, Moon, Newspaper, Plug, ScrollText, Search, ShieldCheck, Shirt, SlidersHorizontal, Sparkles, Sun,
  ThumbsUp, Timer, Volume2, X, type LucideIcon,
} from 'lucide-react';
import type { AdminUser } from '../lib/api';
import { CHANGELOG } from '../lib/changelog';
import { InstallButton } from '../components/InstallButton';
import { Button } from '../ui/Button';
import { Dialog } from '../ui/Dialog';
import { Icon } from '../ui/Icon';
import { MenuButton } from '../ui/Menu';
import { linkClick, navigate } from './router';
import { JumpTo } from './JumpTo';
import { jumpTargets, pathFor, targetHref, type JumpTarget, type NavGroup, type NavItem, type PageId } from './routes';
import type { ResolvedTheme, ThemePreference } from './theme';

const NAV_ICONS: Record<string, LucideIcon> = {
  dashboard: LayoutDashboard,
  support: LifeBuoy,
  'pc-switches': MonitorCog,
  caps: Gauge,
  usage: BarChart3,
  analytics: BarChart3,
  adoption: BarChart3,
  'office-installs': Monitor,
  access: KeyRound,
  knowledge: Library,
  skills: BookOpen,
  pets: Bot,
  'company-skills': Sparkles,
  config: Cpu,
  apps: Boxes,
  voice: Volume2,
  governance: Landmark,
  outfits: Shirt,
  activity: Activity,
  audit: ScrollText,
  approvals: ShieldCheck,
  pulse: HeartPulse,
  'pulse-asks': MessagesSquare,
  'pulse-domains': Layers,
  'pulse-tools': Plug,
  'pulse-feedback': ThumbsUp,
  'pulse-problems': Bug,
  'pulse-digests': Newspaper,
  'pulse-operations': Timer,
  'pulse-settings': SlidersHorizontal,
};

const RECENT_KEY = 'gyro-admin.recent';
const SETTINGS_FOLD_KEY = 'gyro-admin.rail.settings';

function readStored(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // A convenience only: it is simply not remembered.
  }
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const letters = parts.length > 1 ? `${parts[0]![0]}${parts[parts.length - 1]![0]}` : (parts[0] ?? '?').slice(0, 2);
  return letters.toUpperCase();
}

/** Scrolls to the panel whose title starts with `find` once the page has drawn it (pages load their data first). */
function revealSection(find: string): void {
  const started = Date.now();
  const look = () => {
    const heads = Array.from(document.querySelectorAll<HTMLElement>('#main .ui-panel-title'));
    const head = heads.find((el) => (el.textContent ?? '').trim().toLowerCase().startsWith(find.toLowerCase()));
    if (head) {
      const block = head.closest<HTMLElement>('.ui-panel') ?? head;
      block.scrollIntoView({ block: 'start', behavior: 'smooth' });
      block.classList.add('is-revealed');
      window.setTimeout(() => block.classList.remove('is-revealed'), 1600);
      return;
    }
    if (Date.now() - started < 5000) window.setTimeout(look, 120);
  };
  window.setTimeout(look, 0);
}

export interface ShellProps {
  user: AdminUser;
  auditorOnly: boolean;
  page: PageId;
  groups: NavGroup[];
  theme: { preference: ThemePreference; resolved: ResolvedTheme; setPreference: (preference: ThemePreference) => void };
  onSignOut: () => void;
  children: ReactNode;
}

/**
 * The console's frame: the grouped left rail (a top bar and a drawer below 820 px), the Ctrl K "Jump to" box, the
 * auditor banner and What's new. Each page draws its own sticky header (ui/PageHeader) inside the scrolling area.
 */
export function Shell({ user, auditorOnly, page, groups, theme, onSignOut, children }: ShellProps) {
  const [navOpen, setNavOpen] = useState(false);
  const [jumpOpen, setJumpOpen] = useState(false);
  const [whatsNew, setWhatsNew] = useState(false);
  const [recentIds, setRecentIds] = useState<PageId[]>(() => {
    const stored = readStored(RECENT_KEY);
    try {
      const parsed = stored ? (JSON.parse(stored) as unknown) : [];
      return Array.isArray(parsed) ? (parsed.filter((v) => typeof v === 'string') as PageId[]) : [];
    } catch {
      return [];
    }
  });
  const scroller = useRef<HTMLDivElement>(null);
  const firstPage = useRef(true);

  const targets = useMemo(() => jumpTargets(groups), [groups]);
  const recent = useMemo(
    () => recentIds.filter((id) => id !== page).map((id) => targets.find((t) => t.key === `page:${id}`)).filter((t): t is JumpTarget => Boolean(t)).slice(0, 4),
    [recentIds, page, targets],
  );

  // A page change: back to the top, remembered for the Jump to box's "Recent".
  useEffect(() => {
    if (firstPage.current) firstPage.current = false;
    else scroller.current?.scrollTo({ top: 0 });
    setRecentIds((ids) => [page, ...ids.filter((id) => id !== page)].slice(0, 6));
  }, [page]);
  useEffect(() => {
    writeStored(RECENT_KEY, JSON.stringify(recentIds));
  }, [recentIds]);

  // Ctrl K (Cmd K on a Mac) opens Jump to from anywhere in the console.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && !event.altKey && !event.shiftKey && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setNavOpen(false);
        setJumpOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const pick = useCallback((target: JumpTarget) => {
    setJumpOpen(false);
    setNavOpen(false);
    navigate(targetHref(target));
    if (target.find) revealSection(target.find);
  }, []);

  const iconFor = useCallback((target: JumpTarget) => NAV_ICONS[target.page] ?? LayoutDashboard, []);

  const rail = (
    <RailContent
      user={user}
      auditorOnly={auditorOnly}
      page={page}
      groups={groups}
      theme={theme}
      onSignOut={onSignOut}
      onJump={() => { setNavOpen(false); setJumpOpen(true); }}
      onWhatsNew={() => { setNavOpen(false); setWhatsNew(true); }}
      onNavigate={() => setNavOpen(false)}
    />
  );

  return (
    <div className="shell">
      <a className="shell-skip" href="#main">Skip to content</a>
      <aside className="shell-rail" aria-label="Gyro Admin">{rail}</aside>

      <div className="shell-main">
        <header className="shell-topbar">
          <Button variant="ghost" icon={MenuIcon} aria-label="Open navigation" aria-expanded={navOpen} onClick={() => setNavOpen(true)} />
          <Brand theme={theme.resolved} />
          <span className="shell-topbar-spacer" />
          <Button variant="ghost" icon={Search} aria-label="Jump to" onClick={() => setJumpOpen(true)} />
          <span className="shell-avatar is-sm" aria-hidden="true">{initials(user.displayName)}</span>
        </header>
        <div className="shell-scroller" data-scroller ref={scroller}>
          {auditorOnly ? (
            <div className="shell-banner" role="note">
              <Icon icon={ShieldCheck} size="sm" />
              <span><strong>Read-only auditor session.</strong> You can see these pages; the service refuses every change.</span>
            </div>
          ) : null}
          <main id="main" className="shell-page" tabIndex={-1}>{children}</main>
        </div>
      </div>

      <Dialog open={navOpen} onClose={() => setNavOpen(false)} title="Navigation" titleHidden variant="drawer" className="shell-navdrawer" hideClose>
        <div className="shell-navdrawer-close">
          <Button variant="ghost" size="sm" icon={X} aria-label="Close navigation" onClick={() => setNavOpen(false)} />
        </div>
        {rail}
      </Dialog>

      <JumpTo open={jumpOpen} onClose={() => setJumpOpen(false)} targets={targets} recent={recent} iconFor={iconFor} onPick={pick} />

      <Dialog open={whatsNew} onClose={() => setWhatsNew(false)} title="What's new in Gyro Admin" className="shell-whatsnew">
        <ol className="shell-changelog">
          {CHANGELOG.map((entry, index) => (
            <li key={`${entry.version}-${entry.date}-${index}`}>
              <p className="shell-changelog-head">
                <span className="shell-changelog-title">{entry.title}</span>
                <span className="shell-changelog-date">{entry.date}{entry.version !== 'next' ? ` · v${entry.version}` : ''}</span>
              </p>
              <ul>{entry.items.map((item, i) => <li key={i}>{item}</li>)}</ul>
            </li>
          ))}
        </ol>
      </Dialog>
    </div>
  );
}

function Brand({ theme }: { theme: ResolvedTheme }) {
  return (
    <span className="shell-brand">
      <img className="shell-logo" src={theme === 'dark' ? '/brand/exante360-logo-reversed.png' : '/brand/exante360-logo.png'} alt="Exante360" />
      <span className="shell-brand-rule" aria-hidden="true" />
      <span className="shell-product">Gyro Admin</span>
    </span>
  );
}

interface RailContentProps {
  user: AdminUser;
  auditorOnly: boolean;
  page: PageId;
  groups: NavGroup[];
  theme: ShellProps['theme'];
  onSignOut: () => void;
  onJump: () => void;
  onWhatsNew: () => void;
  onNavigate: () => void;
}

function RailContent({ user, auditorOnly, page, groups, theme, onSignOut, onJump, onWhatsNew, onNavigate }: RailContentProps) {
  // Settings is folded until the person opens it (or is on one of its pages); the fold is remembered in this browser.
  const [settingsOpen, setSettingsOpen] = useState(() => readStored(SETTINGS_FOLD_KEY) === 'open');
  const toggleSettings = () => {
    setSettingsOpen((open) => {
      writeStored(SETTINGS_FOLD_KEY, open ? 'closed' : 'open');
      return !open;
    });
  };
  const themeIcon = theme.preference === 'system' ? Monitor : theme.resolved === 'dark' ? Moon : Sun;
  const idBase = useId();

  return (
    <div className="shell-rail-inner">
      <div className="shell-rail-brand"><Brand theme={theme.resolved} /></div>
      <button type="button" className="shell-jump" onClick={onJump} aria-keyshortcuts="Control+K">
        <Icon icon={Search} size="sm" />
        <span>Jump to…</span>
        <kbd className="ui-kbd">Ctrl K</kbd>
      </button>
      <nav className="shell-nav" aria-label="Console">
        {groups.map((group) => {
          const holdsActive = group.items.some((it) => it.pages.includes(page));
          const folded = Boolean(group.collapsible) && !settingsOpen && !holdsActive;
          const listId = `${idBase}-${group.id}`;
          return (
            <div key={group.id} className="shell-nav-group">
              {group.collapsible ? (
                <button type="button" className="shell-nav-label is-toggle" aria-expanded={!folded} aria-controls={listId} onClick={toggleSettings} disabled={holdsActive}>
                  <span>{group.label}</span>
                  <Icon icon={folded ? ChevronRight : ChevronDown} size="xs" />
                </button>
              ) : (
                <p className="shell-nav-label" id={`${listId}-label`}>{group.label}</p>
              )}
              {folded ? null : (
                <ul id={listId} aria-labelledby={group.collapsible ? undefined : `${listId}-label`} aria-label={group.collapsible ? group.label : undefined}>
                  {group.items.map((it) => <NavLink key={it.id} item={it} page={page} onNavigate={onNavigate} />)}
                </ul>
              )}
            </div>
          );
        })}
      </nav>
      <div className="shell-rail-utility">
        <button type="button" className="shell-util" onClick={onWhatsNew}>
          <Icon icon={Sparkles} size="sm" />
          <span>What&apos;s new</span>
        </button>
        <InstallButton />
      </div>
      <div className="shell-rail-foot">
        <span className="shell-avatar" aria-hidden="true">{initials(user.displayName)}</span>
        <span className="shell-who">
          <span className="shell-who-name">{user.displayName}</span>
          <span className="shell-who-role">{auditorOnly ? 'Auditor · read-only' : 'IT admin'}</span>
        </span>
        <MenuButton
          label="Theme"
          heading="Theme"
          icon={themeIcon}
          placement="up"
          items={[
            { id: 'system', label: 'Follow Windows', icon: Monitor, checked: theme.preference === 'system', onSelect: () => theme.setPreference('system') },
            { id: 'light', label: 'Light', icon: Sun, checked: theme.preference === 'light', onSelect: () => theme.setPreference('light') },
            { id: 'dark', label: 'Dark', icon: Moon, checked: theme.preference === 'dark', onSelect: () => theme.setPreference('dark') },
          ]}
        />
        <Button variant="ghost" size="sm" icon={LogOut} aria-label="Sign out" title="Sign out" onClick={onSignOut} />
      </div>
    </div>
  );
}

function NavLink({ item, page, onNavigate }: { item: NavItem; page: PageId; onNavigate: () => void }) {
  const active = item.pages.includes(page);
  return (
    <li>
      <a
        href={pathFor(item.pages[0]!)}
        className={`shell-nav-item${active ? ' is-active' : ''}`}
        aria-current={active ? 'page' : undefined}
        onClick={(event) => linkClick(event, onNavigate)}
      >
        <Icon icon={NAV_ICONS[item.id] ?? LayoutDashboard} size="md" />
        <span>{item.label}</span>
      </a>
    </li>
  );
}
